// The sketch's DATA CONTRACT, alone. How a sketch is fetched lives next door in
// `focus/figma/useDesignScene.ts`; how one is drawn lives in
// `focus/figma/sceneRender.tsx`. This file is only the shape.
//
// A `DesignScene` is a DRAW LIST, never a design: numbers, a closed set of
// shape words, Loom's own icon ids, a vector's own outline as SVG path data, a
// gradient's colour stops, and the text the screen actually displays. No layer
// name is in it — the icon match runs server-side and only the matched
// primitive id ships, so a layer called "Verbinde dein GitLab-Konto erneut"
// reaches this process as an anonymous box. That is the whole safety spine of
// the preview and it lives in the PAYLOAD, not in an escaper.
//
// WHY IT SITS IN `lib/` AND NOT IN `focus/figma/`. A scene now reaches the
// browser two ways, and the second one is folded in `chat/useWsSession.ts`:
//
//   1. ITS OWN CHAT EVENT (`figma_scene`), replayed in full on every attach.
//      This is the picture the conversation actually had — the bytes the read
//      was about, kept with the chat, not the newest copy of the frame.
//   2. A credentialed GET (`…/figma/preview/:previewId`), the FALLBACK: a chat
//      from before the event existed, a scene the event log's FIFO cap evicted,
//      a `sceneVersion` this bundle refuses, an older client.
//
// A module under `focus/` cannot be the home of a shape the chat fold parses:
// `chat/` would be importing a surface it does not render. So the shape moved
// here and `useDesignScene.ts` re-exports every name, which is why no existing
// import path changed.
//
// WHY THE SCHEMA IS HAND-COPIED. This mirrors `@loom/shared/figma`'s
// `designSceneSchema` the same way `lib/ws.ts` hand-copies `widgetSpecSchema`,
// and for the same two reasons: the shared module that owns the projector is
// ~2000 lines the web bundle has never carried, and a VALUE import (a zod
// schema is a value) cannot be erased by `import type`. The mirror is
// deliberately PERMISSIVE — an unknown `kind` or icon id costs one shape, never
// the drawing — and every field the server may send must be declared here or
// zod strips it with no type error and no failing test (the `gateRefusal` /
// `draft` lesson).
//
// THE MIRROR IS THE GATE ON BOTH LANES. The GET parses through it, and so does
// the `figma_scene` fold — `lib/ws.ts` passes the scene through as `unknown`
// on purpose, so a scene shape this bundle cannot read costs the ENTRY (and
// falls back to the GET, which re-projects) rather than the whole frame and
// every other effect in it.

import { z } from "zod";

/**
 * Bumped by the projector, not by this file. Sent back on the wire so a client
 * that predates a shape can say so rather than mis-draw it.
 *
 * A HAND-KEPT MIRROR OF `@loom/shared/figma/scene`'s constant, and the only one
 * in this file that is LOAD-BEARING rather than descriptive: `useWsSession`'s
 * fold hard-drops any stored scene whose `sceneVersion` is not this number.
 * Bump shared's and forget this line and EVERY stored scene is silently
 * discarded — every chat quietly falls back to the GET's newest-copy bytes,
 * which is the exact defect the stored scene exists to close — with all four
 * gates green. `design-scene.version-parity.test.ts` imports both constants and
 * fails on the drift, because nothing in the type system can.
 */
export const SCENE_VERSION = 2;

/** `#rrggbb`, lower case. The ONE shape a colour may have before it is allowed
 *  near a `fill` / `stroke` attribute. Re-asserted at the render seam too — the
 *  guard sits at both ends of the wire because CSP's `style-src` carries
 *  `unsafe-inline` and cannot catch a raw string in a style value. */
const HEX6 = /^#[0-9a-f]{6}$/;

/** A colour from the payload, lower-cased, or `null` when it is not a plain
 *  six-digit hex. `null` means "draw without this attribute", never "throw". */
export const asHexColor = (value: string | undefined): string | null => {
  if (value === undefined) return null;
  const lower = value.toLowerCase();
  return HEX6.test(lower) ? lower : null;
};

/** The shape words a scene node may wear. Kept as a bare string on the wire
 *  (see `sceneNodeSchema`); this is the set this client can draw. */
export type SceneKind =
  | "box"
  | "ellipse"
  | "line"
  | "image"
  | "icon"
  | "path"
  | "text";

/** One colour stop. Bare numbers and strings on purpose — the renderer
 *  re-checks every one before it reaches an attribute. */
export interface SceneGradientStop {
  readonly o: number;
  readonly c: string;
  readonly a?: number;
}

/** A gradient: `k` is `l` or `r`, `p` the start/end (or centre/radius) handles
 *  in the node's own 0..1 space, `s` the stops in order. */
export interface SceneGradient {
  readonly k: string;
  readonly p: readonly number[];
  readonly s: readonly SceneGradientStop[];
}

export interface SceneNode {
  /** Bare string on the wire on purpose — a shape word from a newer projector
   *  costs one node, never the frame. */
  readonly kind: string;
  /** Frame-relative and already clamped to the frame server-side. */
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly fill?: string;
  /** On a `path`, the colour the stroke OUTLINE (`sd`) is filled with. */
  readonly stroke?: string;
  /** On an `image`, `min(w,h)/2` means round — drawn as a dashed ellipse. */
  readonly radius?: number;
  /** Radians, present only when non-zero. */
  readonly rot?: number;
  /**
   * 0..1. How visible the shape is, and how sure the projector is of it: the
   * paint's own alpha times the node's opacity chain, and — dimmer still — a
   * vector Loom holds no drawable outline for, or a gradient whose stops it
   * could not read, because a solid box would claim more than the bytes
   * support. Absent means "draw it plainly"; below 1% means "draw nothing".
   *
   * NAMED FOR THE WIRE, like every other field here. The server emits `op`
   * (`SceneNode.op`, `@loom/shared/figma/scene`), and this mirror is permissive
   * — an unknown key is dropped in silence, so a mirror that spelled it
   * `opacity` would parse, pass its own tests, and simply never fade anything.
   */
  readonly op?: number;
  /** Loom's own icon id, matched SERVER-side from the layer name. A name never
   *  ships; an unmatched one ships as a plain box with no string at all. */
  readonly icon?: string;
  /** Text only — already wrapped to the node's box server-side, so the browser
   *  needs no `<foreignObject>` to lay it out (which would reintroduce an HTML
   *  sink into a subtree whose whole point is that it has none). */
  readonly lines?: readonly string[];
  readonly size?: number;
  readonly lh?: number;
  readonly weight?: number;
  readonly align?: "left" | "center" | "right";
  readonly track?: number;
  /** A gradient paint, drawn in place of `fill`. */
  readonly grad?: SceneGradient;
  /** `path` only — the vector's own outline, as SVG path data in its LOCAL
   *  space: `d` the fill region, `sd` the stroke's outline (a region too, so
   *  it is filled with `stroke`, never stroked). Re-checked against
   *  `SCENE_PATH_RE` and the length cap before either touches an attribute. */
  readonly d?: string;
  readonly dr?: string;
  readonly sd?: string;
  readonly sdr?: string;
  /** `path` only — the local space's orientation, SVG `matrix()` order
   *  `[a, b, c, d]`, no translation: the node's own box places it. */
  readonly m?: readonly number[];
  /** `path` only — the local box's size. */
  readonly sw?: number;
  readonly sh?: number;
  /** `path` only — the node's box corner BEFORE the frame clipped it, when the
   *  clip moved it. */
  readonly bx?: number;
  readonly by?: number;
}

export interface SceneCounts {
  /** Every node the walk reached. Deliberately NOT the digest's
   *  `omissions.nodesIncluded`: the digest truncates for a token budget in a
   *  later stage than the one this scene flattens, so the two legitimately
   *  disagree about how many siblings a parent has. */
  readonly nodes: number;
  readonly drawn: number;
  readonly icons: number;
  /** Vector shapes drawn as a box because no outline could be drawn — none
   *  held on the row, or one Loom refuses (too big to keep, a placement it
   *  cannot reproduce) — plus a gradient whose stops could not be read. The
   *  number behind the caption's plain-box line, whose reason ("couldn't draw
   *  its real outline or colours") is true of every one of them. */
  readonly plainBoxes: number;
  /** Vectors drawn from their own outline. Not a compromise, so not in the
   *  caption. Optional: an older server does not send it. */
  readonly outlines?: number;
  /** Shapes drawn with a real gradient. Same rules as `outlines`. */
  readonly gradients?: number;
  readonly texts: number;
  /** Pictures with no stored bytes — drawn as an empty dashed box. */
  readonly images: number;
  /** Layers switched off in the file. A drawing has no "off" marker, so they
   *  are left out and counted instead of silently vanishing. */
  readonly hiddenOmitted: number;
}

export interface DesignScene {
  readonly sceneVersion: number;
  readonly w: number;
  readonly h: number;
  readonly nodes: readonly SceneNode[];
  readonly counts: SceneCounts;
  /**
   * When the bytes this drawing was built from were READ FROM FIGMA, ISO-8601.
   *
   * It rides INSIDE the scene on both lanes, which is what lets the caption not
   * know which lane served it. Off the chat's own `figma_scene` it is the read
   * the conversation was about; off the GET it is the newest stored copy, which
   * may be a later read of the same frame under a chip that still says "read 2
   * days ago". Without the stamp that swap is silent, which is the one thing a
   * widget whose whole claim is "every number on it is backed by the payload"
   * cannot be.
   *
   * OPTIONAL, and absent means "say nothing" — an older server that predates
   * the field must not make the caption row render an empty line. It is a
   * TIMESTAMP, not content: no file key, no folder, no frame name.
   */
  readonly readAtIso?: string;
}

const sceneNodeSchema = z.object({
  kind: z.string(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  fill: z.string().optional().catch(undefined),
  stroke: z.string().optional().catch(undefined),
  radius: z.number().optional().catch(undefined),
  rot: z.number().optional().catch(undefined),
  op: z.number().optional().catch(undefined),
  icon: z.string().optional().catch(undefined),
  lines: z.array(z.string()).optional().catch(undefined),
  size: z.number().optional().catch(undefined),
  lh: z.number().optional().catch(undefined),
  weight: z.number().optional().catch(undefined),
  align: z.enum(["left", "center", "right"]).optional().catch(undefined),
  track: z.number().optional().catch(undefined),
  // Declared or stripped — the `gateRefusal` / `draft` lesson again. Every one
  // permissive: a malformed outline or gradient costs that field (and the
  // renderer then draws the plain box), never the drawing.
  grad: z
    .object({
      k: z.string(),
      p: z.array(z.number()),
      s: z.array(
        z.object({
          o: z.number(),
          c: z.string(),
          a: z.number().optional().catch(undefined),
        }),
      ),
    })
    .optional()
    .catch(undefined),
  d: z.string().optional().catch(undefined),
  dr: z.string().optional().catch(undefined),
  sd: z.string().optional().catch(undefined),
  sdr: z.string().optional().catch(undefined),
  m: z.array(z.number()).optional().catch(undefined),
  sw: z.number().optional().catch(undefined),
  sh: z.number().optional().catch(undefined),
  bx: z.number().optional().catch(undefined),
  by: z.number().optional().catch(undefined),
});

const countsSchema = z.object({
  nodes: z.number().int().catch(0),
  drawn: z.number().int().catch(0),
  icons: z.number().int().catch(0),
  plainBoxes: z.number().int().catch(0),
  texts: z.number().int().catch(0),
  images: z.number().int().catch(0),
  hiddenOmitted: z.number().int().catch(0),
  outlines: z.number().int().catch(0),
  gradients: z.number().int().catch(0),
});

/**
 * The route boundary's own assertion, restated in the browser.
 *
 * `w`/`h` are bounded here as well as server-side: a corrupt or hostile blob
 * that reported a 99999px frame would otherwise paint a drawing far outside the
 * Focus column. The server refuses it first; this is the second of the two ends
 * the guard sits at — and since the `figma_scene` lane hands this schema a value
 * `lib/ws.ts` never inspected, on that lane it is the ONLY end.
 */
const designSceneSchemaRaw = z.object({
  sceneVersion: z.number().int(),
  w: z.number().positive().max(20000),
  h: z.number().positive().max(20000),
  nodes: z.array(sceneNodeSchema).max(2000),
  counts: countsSchema,
  // Declared here or it does not exist in the browser. This mirror is not a
  // type-level view of the server's schema: zod STRIPS a key it was not told
  // about, so a field the server really sends arrives, parses, and is gone —
  // with no type error and no failing test. That is the `gateRefusal` / `draft`
  // lesson this file's header names, and the age line is the next thing it
  // would have eaten. Permissive on purpose: a malformed timestamp costs the
  // one line, never the drawing.
  readAtIso: z.string().optional().catch(undefined),
});

/** Annotated the way `lib/ws.ts` annotates `agentEventSchema`, and for the same
 *  reason: `.catch(undefined)` widens a field's inferred output to `unknown`,
 *  so the raw schema's `z.infer` is not the hand-written interface even when
 *  the two agree exactly. The INTERFACE is the contract the rest of the module
 *  reads; the schema is the runtime assertion. */
export const designSceneSchema =
  designSceneSchemaRaw as unknown as z.ZodType<DesignScene>;
